import { useCallback, useEffect, useState } from "react";
import { Loader2, ScrollText, ShieldAlert } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Chip, Empty, Err, Ok, Panel, Tag } from "@/components/community/kit";
import { ROLE_TONE, allows, when } from "@/lib/community";

const AREAS = [["chat", "Chat"], ["members", "Members"], ["elections", "Elections & motions"],
  ["money", "Money"], ["settings", "Settings"], ["moderate", "Moderation"]];
const LEVELS = [["none", "No access"], ["view", "Can see"], ["limited", "Can take part"],
  ["full", "Full control"]];
const EDITABLE = ["leader", "treasurer", "moderator", "member"];

export const Rules = ({ community, onChanged }) => {
  const cid = community.id;
  const [form, setForm] = useState({
    description: community.description || "", rules: community.rules || "",
    city: community.city || "", state: community.state || "",
    membership: community.membership || "approval", listed: community.listed,
    election_frequency: community.election_frequency || "annual",
  });
  const [matrix, setMatrix] = useState(community.permissions || {});
  const [leadership, setLeadership] = useState([]);
  const [audit, setAudit] = useState([]);
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const canEdit = allows(community, "settings", "limited") || community.platform_admin;
  const canFull = allows(community, "settings", "full") || community.platform_admin;
  const canModerate = allows(community, "moderate", "full") || community.platform_admin;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/communities/${cid}/leadership`);
      setLeadership(data.leadership);
      if (canEdit) {
        const { data: a } = await api.get(`/communities/${cid}/audit`, { params: { limit: 60 } });
        setAudit(a.entries);
      }
      if (canModerate) {
        const { data: r } = await api.get(`/communities/${cid}/reports`);
        setReports(r.reports);
      }
    } catch (e) { setError(errMsg(e)); }
  }, [cid, canEdit, canModerate]);

  useEffect(() => { load(); }, [load]);

  const save = async (payload, key) => {
    setBusy(key); setError(""); setNotice("");
    try {
      await api.patch(`/communities/${cid}`, payload);
      setNotice("Saved.");
      onChanged?.();
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const handleReport = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/communities/${cid}/reports/${id}`, { action, minutes: 1440 });
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="community-rules" className="space-y-4">
      <Err testId="rules-error">{error}</Err>
      <Ok testId="rules-notice">{notice}</Ok>

      <Panel testId="rules-leadership" title="Who runs this community">
        {leadership.length === 0 ? (
          <Empty testId="leadership-empty">No officers yet.</Empty>
        ) : (
          <ul className="divide-y divide-ink-600">
            {leadership.map((m) => (
              <li key={m.id} data-testid={`leadership-row-${m.id}`}
                className="flex flex-wrap items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-100">{m.name}</p>
                  {(m.positions || []).length > 0 && (
                    <p className="truncate text-xs text-slate-500">{m.positions.join(" · ")}</p>
                  )}
                </div>
                <Chip className={ROLE_TONE[m.role]}>{m.role_label}</Chip>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {!canEdit ? (
        <Panel testId="rules-read" title="The rules">
          {community.rules
            ? <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-300">{community.rules}</p>
            : <Empty testId="rules-empty">The leadership has not written rules yet.</Empty>}
        </Panel>
      ) : (
        <>
          <Panel testId="rules-edit" title="About & rules">
            <div className="space-y-3">
              <Textarea data-testid="rules-description" rows={3} placeholder="One paragraph about this community"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <Textarea data-testid="rules-text" rows={8} placeholder="House rules, conduct, how disputes are handled…"
                value={form.rules} onChange={(e) => setForm({ ...form, rules: e.target.value })} />
              <div className="grid gap-3 sm:grid-cols-2">
                <Input data-testid="rules-city" placeholder="City" value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })} />
                <Input data-testid="rules-state" placeholder="State" value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })} />
              </div>
              <Button data-testid="rules-save" onClick={() => save({
                description: form.description, rules: form.rules, city: form.city, state: form.state,
              }, "rules")} disabled={busy === "rules"}>
                {busy === "rules" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <ScrollText className="mr-2 h-4 w-4" />}
                Save
              </Button>
            </div>
          </Panel>

          {canFull && (
            <>
              <Panel testId="rules-membership" title="Joining & visibility">
                <div className="space-y-3">
                  <select data-testid="rules-mode" value={form.membership}
                    onChange={(e) => setForm({ ...form, membership: e.target.value })}
                    className="h-10 w-full rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-200">
                    <option value="open">Open — any chauffeur or office user walks straight in</option>
                    <option value="approval">Approval — a leader lets each person in</option>
                    <option value="invite">Invitation only</option>
                  </select>
                  <select data-testid="rules-frequency" value={form.election_frequency}
                    onChange={(e) => setForm({ ...form, election_frequency: e.target.value })}
                    className="h-10 w-full rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-200">
                    <option value="none">No fixed election cycle</option>
                    <option value="annual">Elections every year</option>
                    <option value="biennial">Elections every two years</option>
                    <option value="custom">Custom cycle</option>
                  </select>
                  <label className="flex items-center gap-2 text-sm text-slate-400">
                    <input data-testid="rules-listed" type="checkbox" className="h-4 w-4"
                      checked={Boolean(form.listed)}
                      onChange={(e) => setForm({ ...form, listed: e.target.checked })} />
                    List us in the directory so drivers can find us
                  </label>
                  <Button data-testid="rules-membership-save" onClick={() => save({
                    membership: form.membership, listed: form.listed,
                    election_frequency: form.election_frequency,
                  }, "membership")} disabled={busy === "membership"}>
                    Save
                  </Button>
                </div>
              </Panel>

              <Panel testId="rules-permissions" title="What each role can do"
                sub="The Community Owner always keeps full control — that row cannot be changed.">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[620px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-ink-600 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                        <th className="py-2">Area</th>
                        {EDITABLE.map((r) => <th key={r} className="py-2">{r}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {AREAS.map(([area, label]) => (
                        <tr key={area} className="border-b border-ink-700/60">
                          <td className="py-2 text-slate-300">{label}</td>
                          {EDITABLE.map((role) => (
                            <td key={role} className="py-2 pr-2">
                              <select data-testid={`perm-${role}-${area}`}
                                value={matrix?.[role]?.[area] || "none"}
                                onChange={(e) => setMatrix({
                                  ...matrix,
                                  [role]: { ...(matrix[role] || {}), [area]: e.target.value },
                                })}
                                className="h-9 w-full rounded-md border border-ink-600 bg-ink-900 px-2 text-xs text-slate-200">
                                {LEVELS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                              </select>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button data-testid="perm-save" className="mt-3"
                  onClick={() => save({ permissions: matrix }, "perms")} disabled={busy === "perms"}>
                  {busy === "perms" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Save permissions
                </Button>
              </Panel>
            </>
          )}
        </>
      )}

      {canModerate && reports.length > 0 && (
        <Panel testId="rules-reports" title={`Reported messages · ${reports.length}`}>
          <ul className="space-y-3">
            {reports.map((r) => (
              <li key={r.id} data-testid={`report-row-${r.id}`}
                className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
                <p className="flex items-center gap-2 text-xs text-amber-300">
                  <ShieldAlert className="h-3.5 w-3.5" /> {r.reporter} reported {r.author}: {r.reason}
                </p>
                <p className="mt-2 rounded bg-ink-900 p-2 text-sm text-slate-300">{r.body}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[["dismiss", "Leave it"], ["delete", "Delete it"],
                    ["delete_and_mute", "Delete & mute 24h"]].map(([action, label]) => (
                    <Button key={action} data-testid={`report-${action}-${r.id}`} size="sm"
                      variant="outline" onClick={() => handleReport(r.id, action)}
                      disabled={busy === r.id}>
                      {label}
                    </Button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {canEdit && audit.length > 0 && (
        <Panel testId="rules-audit" title="Everything that has been done here">
          <ul className="divide-y divide-ink-600">
            {audit.map((a, i) => (
              <li key={i} data-testid={`audit-row-${i}`} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <p className="text-sm text-slate-300">
                  <span className="font-mono text-xs text-gold">{a.action}</span> · {a.actor}
                </p>
                <span className="font-mono text-[10px] text-slate-600">{when(a.at)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Tag>Community code {community.code} · created {when(community.created_at)}</Tag>
    </div>
  );
};
